import type { EligibilityCheckResponse } from '../../services/api';

/**
 * How stale a completed check is allowed to be before we nudge for a re-check.
 *
 * Mirrors `has_recent_completed_eligibility_check`'s 30-day default on the
 * backend, which the auth queue already uses.
 */
export const ELIGIBILITY_STALE_AFTER_DAYS = 30;

export type EligibilityFreshness =
  'none' | 'pending' | 'fresh' | 'stale' | 'failed';

/**
 * The status a check really has, which is not always the one on the column.
 *
 * A 271 can come back `completed` and still be a rejection: the payer's answer
 * carries `raw_response.error` or a non-empty `errors` array. Shared with the
 * intake step, which labels it "Completed with error".
 */
export type EffectiveEligibilityStatus =
  EligibilityCheckResponse['status'] | 'completed_with_error';

export function getEffectiveStatus(
  check: EligibilityCheckResponse
): EffectiveEligibilityStatus {
  if (
    check.status === 'completed' &&
    (check.raw_response?.error ||
      ((check.raw_response?.errors as Array<Record<string, unknown>>) || [])
        .length > 0)
  ) {
    return 'completed_with_error';
  }
  return check.status;
}

/**
 * Parse a check timestamp as the UTC instant it is.
 *
 * `request_date` / `response_date` are naive UTC columns, so they serialize
 * without an offset and `new Date(...)` would read them as local time.
 */
export function parseCheckInstant(value: string): Date {
  const hasZone = value.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(value);
  return new Date(hasZone ? value : `${value}Z`);
}

/** The most recent check wins; checks without a response date have not landed. */
export function latestCheck(
  checks: EligibilityCheckResponse[]
): EligibilityCheckResponse | null {
  const dated = checks.filter(c => c.response_date ?? c.request_date);
  if (dated.length === 0) return null;
  return dated.reduce((newest, c) =>
    parseCheckInstant(c.response_date ?? c.request_date) >
    parseCheckInstant(newest.response_date ?? newest.request_date)
      ? c
      : newest
  );
}

export function freshnessOf(
  check: EligibilityCheckResponse | null
): EligibilityFreshness {
  if (!check) return 'none';

  const status = getEffectiveStatus(check);

  // None of these verified anything, and reading them as a green "Verified" is
  // the one thing this line must never do.
  if (
    status === 'failed' ||
    status === 'completed_with_error' ||
    status === 'expired' ||
    status === 'cancelled'
  ) {
    return 'failed';
  }
  // A check that was sent but has not landed is not the same as never having
  // checked: showing "Not verified" invites a second, separately billed 270.
  if (status !== 'completed' || !check.response_date) return 'pending';

  const ageMs = Date.now() - parseCheckInstant(check.response_date).getTime();
  const ageDays = ageMs / (1000 * 60 * 60 * 24);
  return ageDays > ELIGIBILITY_STALE_AFTER_DAYS ? 'stale' : 'fresh';
}
